import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Search, X, Building2, Mail, Phone,
  ChevronRight, Filter, Loader2
} from "lucide-react";
import { CONTACT_SOURCES, CONTACT_SOURCE_LABELS } from "../lib/constants";

const emptyContact = { first_name: "", last_name: "", role: "", email: "", phone: "", linkedin: "", notes: "", company_id: "", source: "manual" };

export default function ContactsPage() {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyContact });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [cRes, compRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setContacts(cRes.data);
      setCompanies(compRes.data);
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  };

  const filtered = contacts.filter(c => {
    if (search && !c.full_name.toLowerCase().includes(search.toLowerCase()) && !c.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterSource && c.source !== filterSource) return false;
    if (filterCompany && c.company_id !== filterCompany) return false;
    return true;
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, company_id: form.company_id || null };
      await axios.post(`${API}/contacts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contact created");
      setShowCreate(false);
      setForm({ ...emptyContact });
      fetchData();
    } catch { toast.error("Failed to create contact"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white">Contacts</h1>
          <p className="text-white/40 text-sm mt-1">{contacts.length} contacts</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-accent flex items-center gap-2" data-testid="create-contact-btn">
          <Plus className="w-4 h-4" /> New Contact
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..." className="form-input pl-10 w-full" data-testid="contacts-search" />
        </div>
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="form-input w-auto min-w-[140px]" data-testid="filter-source">
          <option value="">All Sources</option>
          {CONTACT_SOURCES.map(s => <option key={s} value={s}>{CONTACT_SOURCE_LABELS[s]}</option>)}
        </select>
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="form-input w-auto min-w-[160px]" data-testid="filter-company">
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
      </div>

      {/* Contact List */}
      <div className="space-y-2" data-testid="contacts-list">
        {filtered.length === 0 && (
          <div className="glass-card-static p-12 text-center">
            <Users className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/40">No contacts found</p>
          </div>
        )}
        {filtered.map(contact => (
          <Link key={contact.id} to={`/contacts/${contact.id}`} className="block" data-testid={`contact-row-${contact.id}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card-static p-4 flex items-center gap-4 hover:bg-white/[0.04] transition-colors cursor-pointer group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A96E]/30 to-[#C9A96E]/30 flex items-center justify-center text-white/80 font-semibold text-sm shrink-0">
                {contact.first_name?.[0]}{contact.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{contact.full_name}</p>
                <p className="text-white/40 text-sm truncate">{contact.role}{contact.company_name ? ` · ${contact.company_name}` : ""}</p>
              </div>
              <div className="hidden md:flex items-center gap-4 text-white/30 text-sm">
                {contact.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{contact.email}</span>}
                {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{contact.phone}</span>}
              </div>
              <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-white/5 text-white/30">{CONTACT_SOURCE_LABELS[contact.source] || contact.source}</span>
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Create Panel */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setShowCreate(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-[#0A0A0A] border-l border-white/10 z-50 overflow-y-auto" data-testid="create-contact-panel">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl text-white">New Contact</h2>
                  <button onClick={() => setShowCreate(false)} className="p-2 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="form-label">First Name *</label><input value={form.first_name} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} className="form-input" required data-testid="contact-first-name" /></div>
                    <div><label className="form-label">Last Name *</label><input value={form.last_name} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} className="form-input" required data-testid="contact-last-name" /></div>
                  </div>
                  <div><label className="form-label">Role / Title</label><input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="form-input" placeholder="e.g. General Manager" data-testid="contact-role" /></div>
                  <div><label className="form-label">Email</label><input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="form-input" data-testid="contact-email" /></div>
                  <div><label className="form-label">Phone</label><input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="form-input" data-testid="contact-phone" /></div>
                  <div><label className="form-label">LinkedIn</label><input value={form.linkedin} onChange={e => setForm(p => ({ ...p, linkedin: e.target.value }))} className="form-input" placeholder="https://linkedin.com/in/..." data-testid="contact-linkedin" /></div>
                  <div><label className="form-label">Company</label>
                    <select value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))} className="form-input" data-testid="contact-company">
                      <option value="">No Company</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                  </div>
                  <div><label className="form-label">Source</label>
                    <select value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="form-input" data-testid="contact-source">
                      {CONTACT_SOURCES.map(s => <option key={s} value={s}>{CONTACT_SOURCE_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="form-input min-h-[80px]" data-testid="contact-notes" /></div>
                  <button type="submit" disabled={saving} className="btn-accent w-full flex items-center justify-center gap-2" data-testid="save-contact-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Contact
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
